import { differenceInCalendarDays, parseISO, startOfToday } from 'date-fns';
import { z } from 'zod';

import type { CampaignRestrictions } from '../../types';

export interface SchemaRestrictions {
  minLeadDays: number;
  minClippingBudget: number;
  minInfluencerBudget: number;
}

const DEFAULT_RESTRICTIONS: SchemaRestrictions = {
  minLeadDays: 3,
  minClippingBudget: 500,
  minInfluencerBudget: 2000,
};

/** Default lead-time floor (days); kept in sync with `createCampaignSchema` fallback. */
export const DEFAULT_CREATE_CAMPAIGN_MIN_LEAD_DAYS =
  DEFAULT_RESTRICTIONS.minLeadDays;

export function restrictionsToSchema(
  restrictions: CampaignRestrictions,
): SchemaRestrictions {
  return {
    minLeadDays: restrictions.clipping.min_lead_time_in_days,
    minClippingBudget: restrictions.clipping.min_budget / 100,
    minInfluencerBudget: restrictions.influencer.min_budget / 100,
  };
}

const baseSchema = z.object({
  campaignStartDate: z
    .string()
    .min(1, 'Start date is required')
    .pipe(z.iso.date('Must be a valid date (YYYY-MM-DD)')),
  advertiser: z.string().min(1, 'Advertiser is required'),
  artist: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Artist is required')),
  track: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Track is required')),
  isrc: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .pipe(
      z
        .string()
        .min(1, 'ISRC is required')
        .regex(/^[A-Z]{2}[A-Z0-9]{3}\d{7}$/, 'Must be a valid ISRC format'),
    ),
  spotifyUrl: z
    .string()
    .min(1, 'Spotify URL is required')
    .pipe(z.url('Must be a valid URL')),
  tiktokLink: z
    .string()
    .optional()
    .transform((v) => (v ? v.trim() : ''))
    .pipe(z.union([z.literal(''), z.url('Must be a valid URL')])),
  instagramLink: z
    .string()
    .optional()
    .transform((v) => (v ? v.trim() : ''))
    .pipe(z.union([z.literal(''), z.url('Must be a valid URL')])),
  clippingEnabled: z.boolean(),
  clippingBudget: z.number().optional(),
  clippingPurchaseOrder: z.string().optional(),
  influencerEnabled: z.boolean(),
  influencerBudget: z.number().optional(),
  influencerPurchaseOrder: z.string().optional(),
});

export function createCampaignSchema(
  restrictions: SchemaRestrictions = DEFAULT_RESTRICTIONS,
) {
  const { minLeadDays, minClippingBudget, minInfluencerBudget } = restrictions;

  return baseSchema.superRefine((data, ctx) => {
    if (data.clippingEnabled) {
      if (!data.clippingBudget || data.clippingBudget < minClippingBudget) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${minClippingBudget.toLocaleString()}`,
          path: ['clippingBudget'],
        });
      }

      if (!data.clippingPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['clippingPurchaseOrder'],
        });
      }
    }

    if (data.influencerEnabled) {
      if (
        !data.influencerBudget ||
        data.influencerBudget < minInfluencerBudget
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${minInfluencerBudget.toLocaleString()}`,
          path: ['influencerBudget'],
        });
      }

      if (!data.influencerPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['influencerPurchaseOrder'],
        });
      }
    }

    const hasChannel = data.clippingEnabled || data.influencerEnabled;

    if (!hasChannel) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign channel must be enabled',
        path: ['clippingEnabled'],
      });

      return;
    }

    if (!data.campaignStartDate) {
      return;
    }

    const today = startOfToday();
    const start = parseISO(data.campaignStartDate);

    const leadDays = differenceInCalendarDays(start, today);

    if (leadDays < minLeadDays) {
      ctx.addIssue({
        code: 'custom',
        message: `Start date must be at least ${minLeadDays} days from today`,
        path: ['campaignStartDate'],
      });
    }
  });
}

export type CreateCampaignFormValues = z.input<
  ReturnType<typeof createCampaignSchema>
>;
